import api from '../api/axios';
import { FileDataService } from './fileDataService';

export interface OrganisationDocument {
  id: number;
  unique_id: string;
  display_name: string;
  description: string | null;
  document_date: string | null;
  updated_at: string;
  // from the joined upload
  upload_id: number;
  file_name: string;
  file_type: string;
  uploaded_at: string;
  created_by_name: string | null;
}

export interface OrganisationDocumentMetadata {
  display_name?: string;
  description?: string | null;
  document_date?: string | null;
}

export class OrganisationDocumentDataService {
  static async list(): Promise<OrganisationDocument[]> {
    const res = await api.get('/organisation-documents');
    return res.data as OrganisationDocument[];
  }

  /** Short-lived inline URL backing the in-app PDF.js viewer. */
  static async getViewUrl(id: number): Promise<string> {
    const res = await api.get(`/organisation-documents/${id}/view`);
    return res.data.url as string;
  }

  /** Attachment URL for a normal download. Staff only (server-enforced). */
  static async getDownloadUrl(id: number): Promise<string> {
    const res = await api.get(`/organisation-documents/${id}/download`);
    return res.data.url as string;
  }

  static async update(id: number, metadata: OrganisationDocumentMetadata): Promise<void> {
    await api.put(`/organisation-documents/${id}`, metadata);
  }

  static async remove(id: number): Promise<void> {
    await api.delete(`/organisation-documents/${id}`);
  }

  /**
   * Full upload flow for one PDF: get a presigned PUT URL, upload straight to
   * S3, then create the upload + organisation_document rows. Admin only
   * (server-enforced). Org documents aren't tied to a child, so the presigned
   * URL uses the 'org-document' category (no childUniqueId).
   */
  static async upload(file: File, displayName: string): Promise<void> {
    if (file.type !== 'application/pdf') {
      throw new Error('Only PDF documents are supported');
    }
    // childUniqueId is irrelevant for org documents; the server ignores it for
    // the 'org-document' category and keys the file under the org prefix.
    const { signedUrl, fileKey } = await FileDataService.getPresignedUrl(
      file.name,
      file.type,
      '',
      'org-document',
    );
    await FileDataService.uploadToS3(signedUrl, file);
    await api.post('/organisation-documents', {
      display_name: displayName,
      upload: { file_name: file.name, file_type: file.type, s3_key: fileKey },
    });
  }
}
